import type { AppType } from "@purlink/api/app";
import { isErrorBody, type ErrorBody } from "@purlink/shared/errors";
import { hc, type ClientResponse } from "hono/client";
import type { SuccessStatusCode } from "hono/utils/http-status";

/** Typed client for the Purlink API (same origin, cookie auth). */
export const api = hc<AppType>("/", {
  init: { credentials: "same-origin" },
});

/** Error thrown for non-2xx API responses, carrying the error envelope. */
export class ApiError extends Error {
  readonly status: number;
  readonly body: ErrorBody["error"] | undefined;

  constructor(status: number, body: ErrorBody["error"] | undefined) {
    super(body?.message ?? `Request failed with status ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

/** The JSON body type of the successful (2xx) responses in a route's union. */
export type SuccessBody<R> =
  R extends ClientResponse<infer T, infer S, "json">
    ? S extends SuccessStatusCode
      ? T
      : never
    : never;

/** Resolves a Hono client response to its JSON body, throwing ApiError on failure. */
export async function unwrap<R extends ClientResponse<unknown, number, string>>(
  promise: Promise<R>,
): Promise<SuccessBody<R>> {
  const res = await promise;
  if (res.ok) return (await res.json()) as SuccessBody<R>;
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    body = undefined;
  }
  throw new ApiError(res.status, isErrorBody(body) ? body.error : undefined);
}
